import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, Lock, ShieldAlert, Unlock, Users } from "lucide-react";
import { toast } from "sonner";
import { StatCard, SectionHeader } from "@/components/admin/AdminUI";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";
import {
  useGovernedAccounts,
  useSetAccountStatus,
  type AccountStatus,
  type GovernedAccount,
} from "@/hooks/admin/useAdminGovernance";

const statusTone: Record<AccountStatus, string> = {
  active: "bg-emerald-600 text-white",
  suspended: "bg-red-600 text-white",
  pending_review: "bg-amber-600 text-white",
};

const AccountControls = () => {
  const { data: accounts = [], isLoading, error, refetch } = useGovernedAccounts();
  const setStatus = useSetAccountStatus();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"all" | "creator" | "venue" | "groover">("all");

  const counts = useMemo(
    () => ({
      total: accounts.length,
      pending: accounts.filter((a) => a.account_status === "pending_review").length,
      locked: accounts.filter((a) => a.account_status === "suspended").length,
      active: accounts.filter((a) => a.account_status === "active").length,
    }),
    [accounts],
  );

  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    return accounts
      .filter((a) => (tab === "all" ? true : (a.user_type ?? "groover") === tab))
      .filter((a) =>
        !t ? true : [a.username, a.full_name, a.business_name, a.portal_id].some((v) => (v ?? "").toLowerCase().includes(t)),
      );
  }, [accounts, tab, search]);

  const act = (account: GovernedAccount, status: AccountStatus) =>
    setStatus.mutate(
      { account, status },
      {
        onSuccess: () =>
          toast.success(
            status === "active" ? "Account approved and unlocked" : status === "suspended" ? "Account locked" : "Account flagged for review",
          ),
        onError: (e) => toast.error(e instanceof Error ? e.message : "Could not update the account"),
      },
    );

  if (isLoading) return <LoadingState label="Loading accounts…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Account controls"
        description="Approve new creators and venues, flag risky accounts and lock access. No banking data is exposed on this page."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Accounts" value={counts.total} icon={Users} />
        <StatCard label="Active" value={counts.active} icon={CheckCircle2} tone="positive" />
        <StatCard label="Awaiting review" value={counts.pending} icon={ShieldAlert} tone="warning" />
        <StatCard label="Locked" value={counts.locked} icon={Lock} tone="danger" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="creator">Creators</TabsTrigger>
            <TabsTrigger value="venue">Venues</TabsTrigger>
            <TabsTrigger value="groover">Groovers</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or portal ID"
          className="w-64"
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Users} title="No accounts found" />
      ) : (
        <div className="space-y-3">
          {rows.map((a) => (
            <Card key={a.id} className="bg-card border-border">
              <CardContent className="p-4 flex flex-wrap items-center gap-4">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={a.avatar_url ?? undefined} />
                  <AvatarFallback>{(a.full_name || a.username).slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-[200px] flex-1">
                  <p className="font-medium text-foreground truncate">{a.business_name || a.full_name || a.username}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    @{a.username} · <span className="font-mono">{a.portal_id ?? "—"}</span>
                    {a.city ? ` · ${a.city}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">{a.user_type ?? "groover"}</Badge>
                  <Badge className={`${statusTone[a.account_status] ?? "bg-muted"} border-0 capitalize`}>
                    {a.account_status.replace("_", " ")}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {a.account_status !== "active" && (
                    <Button size="sm" className="gap-1" disabled={setStatus.isPending} onClick={() => act(a, "active")}>
                      <Unlock className="w-3 h-3" /> Approve
                    </Button>
                  )}
                  {a.account_status !== "pending_review" && (
                    <Button size="sm" variant="outline" className="gap-1" disabled={setStatus.isPending} onClick={() => act(a, "pending_review")}>
                      <ShieldAlert className="w-3 h-3" /> Flag
                    </Button>
                  )}
                  {a.account_status !== "suspended" && (
                    <Button size="sm" variant="outline" className="gap-1 text-red-500" disabled={setStatus.isPending} onClick={() => act(a, "suspended")}>
                      <Lock className="w-3 h-3" /> Lock
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountControls;
